import { Link } from "react-router-dom";
import {
  ArrowRight,
  Banknote,
  ClipboardList,
  Gauge,
  Handshake,
  Lock,
  Search,
  type LucideIcon,
} from "lucide-react";

import OrgPlans from "@/components/OrgPlans";
import { Button } from "@/components/ui/button";
import type { OrgLandingProps } from "@/orgs";
import {
  ACCESS_STEPS,
  CLOSING,
  DISCLAIMER,
  FINANCING,
  HERO,
  OFF_MARKET,
  PILLARS,
  SNAPSHOT,
} from "./content";

/* =============================================================================
   eCashflow Acquisitions — custom landing page, served at /ecfa.

   ECFA's story, our design language. The words come from their own site (see
   content.ts); every surface, colour and type decision comes from the platform
   theme — public-hero-shell, public-glass-card, font-display, Dark · Gold.

   Membership pricing is NOT written here. It renders through OrgPlans from the
   platform's live tiers, so a price change reaches this page automatically.

   Dark · Gold is pinned by the OrgLanding wrapper, exactly as it is on "/".
   ========================================================================== */

const PILLAR_ICONS: Record<(typeof PILLARS)[number]["icon"], LucideIcon> = {
  search: Search,
  gauge: Gauge,
  handshake: Handshake,
};

export default function EcfaLanding({ org }: OrgLandingProps) {
  const registerTo = `/${org.slug}/register`;
  const signInTo = `/${org.slug}/sign-in`;

  return (
    <div className="public-stage">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section
        data-theme="dark"
        style={{ backgroundColor: "transparent" }}
        className="px-4 pt-6 sm:px-6 lg:px-8"
      >
        <div className="public-hero-shell relative w-full overflow-hidden rounded-[1.85rem] px-6 py-12 shadow-glow sm:px-8 sm:py-16">
          <div className="public-grid-lattice absolute inset-0 opacity-45" />
          <div className="absolute -right-20 -top-16 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />

          <div className="relative z-10 w-full">
            <div className="public-pill">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              {org.name}
            </div>

            <h1 className="mt-6 max-w-4xl font-display text-4xl leading-[1.08] text-foreground sm:text-5xl lg:text-6xl">
              {HERO.headlineLead}{" "}
              <span className="text-gradient">{HERO.headlineAccent}</span>
            </h1>

            <p className="mt-5 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
              {HERO.sub}
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link to={registerTo}>
                <Button size="lg" className="w-full shadow-glow sm:w-auto">
                  Get free access
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <a href="#access">
                <Button size="lg" variant="outline" className="w-full border-primary/25 sm:w-auto">
                  How access works
                </Button>
              </a>
            </div>

            {/* ECFA's own equation for a deal that closes. Four terms, so it
                reads as an equation rather than a feature list. */}
            <div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border/60 pt-8">
              {HERO.equation.map((term, i) => (
                <span key={term} className="flex items-center gap-3">
                  {i > 0 && <span className="text-lg font-light text-primary/50">+</span>}
                  <span className="font-display text-xl text-foreground sm:text-2xl">{term}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Three pillars ────────────────────────────────────────────────── */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-5 md:grid-cols-3">
          {PILLARS.map(({ icon, title, body }) => {
            const Icon = PILLAR_ICONS[icon];
            return (
              <div key={title} className="public-glass-card rounded-2xl p-6 sm:p-7">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/12 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-2xl leading-tight text-foreground">{title}</h3>
                <p className="mt-2.5 text-sm leading-6 text-muted-foreground">{body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Off-market inventory ─────────────────────────────────────────── */}
      <section
        id="off-market"
        className="scroll-mt-20 border-y border-border/60 bg-card/40 px-4 py-16 sm:px-6 lg:px-8"
      >
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:items-start">
          <div>
            <div className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary">
              {OFF_MARKET.eyebrow}
            </div>
            <h2 className="mt-2.5 max-w-xl font-display text-3xl leading-tight text-foreground sm:text-4xl">
              {OFF_MARKET.title}
            </h2>
            <p className="mt-4 max-w-xl text-base leading-7 text-muted-foreground">
              {OFF_MARKET.body}
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {OFF_MARKET.stats.map(({ value, label }) => (
                <div key={label} className="border-l-2 border-primary/40 pl-4">
                  <div className="font-display text-2xl leading-tight text-primary">{value}</div>
                  <div className="mt-1 text-[13px] leading-5 text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="public-glass-card rounded-2xl p-6 sm:p-8">
            <p className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-primary">
              Sectors covered
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {OFF_MARKET.sectors.map((sector) => (
                <span
                  key={sector}
                  className="rounded-full border border-border/60 bg-background/40 px-3.5 py-1.5 text-[13px] text-foreground/80"
                >
                  {sector}
                </span>
              ))}
            </div>
            <div className="mt-7 flex items-start gap-3 border-t border-border/60 pt-6">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <p className="text-[13px] leading-6 text-muted-foreground">
                Deal detail and seller introductions are reserved for members. A Master NDA is
                required before confidential information is released.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── How access works ─────────────────────────────────────────────── */}
      <section id="access" className="scroll-mt-20 px-4 py-20 sm:px-6 lg:px-8">
        <div className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary">
          How access works
        </div>
        <h2 className="mt-2.5 max-w-2xl font-display text-3xl leading-tight text-foreground sm:text-4xl">
          Four steps from stranger to seller call.
        </h2>

        {/* Numbered because this genuinely is a sequence — each step gates the
            next one, and the order is the point. */}
        <ol className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {ACCESS_STEPS.map(({ title, body }, i) => (
            <li key={title} className="border-t-2 border-primary/30 pt-5">
              <span className="font-display text-3xl leading-none text-primary/70 tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-3 text-base font-semibold leading-snug text-foreground">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* ── Buyer Acquisition Snapshot ───────────────────────────────────── */}
      <section className="border-y border-border/60 bg-card/40 px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:items-center">
          <div>
            <div className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary">
              {SNAPSHOT.eyebrow}
            </div>
            <h2 className="mt-2.5 max-w-xl font-display text-3xl leading-tight text-foreground sm:text-4xl">
              {SNAPSHOT.title}
            </h2>
            <p className="mt-4 max-w-xl text-base leading-7 text-muted-foreground">
              {SNAPSHOT.problem}
            </p>
            <Link to={registerTo} className="mt-7 inline-block">
              <Button size="lg" className="shadow-glow">
                {SNAPSHOT.cta}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <p className="mt-3 text-[13px] text-muted-foreground">{SNAPSHOT.ctaNote}</p>
          </div>

          <div className="public-glass-card rounded-2xl p-6 sm:p-8">
            <div className="flex items-center gap-2.5">
              <ClipboardList className="h-4 w-4 text-primary" />
              <p className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-primary">
                {SNAPSHOT.reviewedLabel}
              </p>
            </div>
            <ul className="mt-5 grid gap-x-6 gap-y-3 sm:grid-cols-2">
              {SNAPSHOT.reviewed.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-[13px] leading-6 text-foreground/80">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Financing ────────────────────────────────────────────────────── */}
      <section id="financing" className="scroll-mt-20 px-4 py-20 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary">
            {FINANCING.eyebrow}
          </div>
          <h2 className="mt-2.5 font-display text-3xl leading-tight text-foreground sm:text-4xl">
            {FINANCING.title}
          </h2>
          <p className="mt-4 text-base leading-7 text-muted-foreground">{FINANCING.body}</p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FINANCING.products.map(({ name, amount, note }) => (
            <div
              key={name}
              className="flex items-start gap-4 rounded-2xl border border-border/60 bg-card/70 p-5"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/12 text-primary">
                <Banknote className="h-4.5 w-4.5" />
              </div>
              <div className="min-w-0">
                <p className="text-[15px] font-semibold text-foreground">{name}</p>
                <p className="mt-0.5 font-display text-xl leading-tight text-primary">{amount}</p>
                <p className="mt-1.5 text-[13px] leading-5 text-muted-foreground">{note}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Membership plans — the platform's tiers, not ECFA's ──────────── */}
      <OrgPlans
        slug={org.slug}
        tierNames={org.tierNames}
        heading="Choose the access level that matches your search."
      />

      {/* ── Closing CTA ──────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden px-4 py-24 text-center sm:px-6">
        <div className="pointer-events-none absolute left-1/2 top-8 h-72 w-[34rem] -translate-x-1/2 rounded-full bg-primary/12 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-2xl">
          <h2 className="font-display text-3xl leading-tight text-foreground sm:text-[2.6rem]">
            {CLOSING.title}
          </h2>
          <p className="mx-auto mt-4 max-w-md text-base text-muted-foreground">{CLOSING.body}</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to={registerTo}>
              <Button size="lg" className="w-full shadow-glow sm:w-auto">
                Register free account
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to={signInTo}>
              <Button size="lg" variant="outline" className="w-full border-primary/25 sm:w-auto">
                Already a member? Sign in
              </Button>
            </Link>
          </div>

          <p className="mx-auto mt-14 max-w-3xl text-[11.5px] leading-5 text-muted-foreground/70">
            {DISCLAIMER}
          </p>
        </div>
      </section>
    </div>
  );
}
